import { alpha, type Theme } from '@mui/material/styles';
import { rowOptions } from '../../shared/constants/commonRowValues';
import { TrackerDataType } from '../../shared/constants/constants';
import { appPalette, componentPalette, logPalette, neutralPalette, semanticPalette } from '../../theme/palette';
import {
  fontSizes,
  fontWeights,
  letterSpacing,
  lineHeights,
  semanticTypography,
  typography,
  typographyColors,
  typographyTokens,
} from '../../theme/typography';

export const getCalendarPalette = (theme: Theme) => {
  const categories = {
    [TrackerDataType.SERVICE]: logPalette.service.main,
    [TrackerDataType.MEDIA_INTERACTION]: logPalette.mediaInteraction.dark,
    [TrackerDataType.ENGAGEMENT]: logPalette.engagement.dark,
    [TrackerDataType.CONTENT_LOG]: logPalette.content.main,
  };
  const categoryText = {
    [TrackerDataType.SERVICE]: neutralPalette.textPrimary,
    [TrackerDataType.MEDIA_INTERACTION]: neutralPalette.textPrimary,
    [TrackerDataType.ENGAGEMENT]: neutralPalette.textPrimary,
    [TrackerDataType.CONTENT_LOG]: neutralPalette.textPrimary,
  };
  const surfaceBorder = componentPalette.card.border;
  const surfaceBorderStrong = neutralPalette.borderStrong;

  return {
    categories,
    eventPill: {
      [TrackerDataType.SERVICE]: {
        backgroundColor: alpha(logPalette.service.main, 0.14),
        borderColor: logPalette.service.main,
        color: categoryText[TrackerDataType.SERVICE],
        mutedBackgroundColor: alpha(logPalette.service.main, 0.09),
        mutedBorderColor: alpha(logPalette.service.main, 0.5),
        mutedColor: neutralPalette.textSecondary,
      },
      [TrackerDataType.MEDIA_INTERACTION]: {
        backgroundColor: alpha(logPalette.mediaInteraction.light, 0.22),
        borderColor: logPalette.mediaInteraction.dark,
        color: categoryText[TrackerDataType.MEDIA_INTERACTION],
        mutedBackgroundColor: alpha(logPalette.mediaInteraction.light, 0.13),
        mutedBorderColor: alpha(logPalette.mediaInteraction.main, 0.55),
        mutedColor: neutralPalette.textSecondary,
      },
      [TrackerDataType.ENGAGEMENT]: {
        backgroundColor: alpha(logPalette.engagement.main, 0.14),
        borderColor: logPalette.engagement.dark,
        color: categoryText[TrackerDataType.ENGAGEMENT],
        mutedBackgroundColor: alpha(logPalette.engagement.main, 0.09),
        mutedBorderColor: alpha(logPalette.engagement.dark, 0.5),
        mutedColor: neutralPalette.textSecondary,
      },
      [TrackerDataType.CONTENT_LOG]: {
        backgroundColor: alpha(logPalette.content.main, 0.13),
        borderColor: logPalette.content.main,
        color: categoryText[TrackerDataType.CONTENT_LOG],
        mutedBackgroundColor: alpha(logPalette.content.main, 0.08),
        mutedBorderColor: alpha(logPalette.content.light, 0.48),
        mutedColor: neutralPalette.textSecondary,
      },
    },
    entryCard: {
      [TrackerDataType.SERVICE]: {
        backgroundColor: logPalette.service.surface,
        borderColor: logPalette.service.main,
        eyebrowColor: logPalette.service.main,
        titleColor: neutralPalette.textPrimary,
        metaColor: neutralPalette.textSecondary,
      },
      [TrackerDataType.MEDIA_INTERACTION]: {
        backgroundColor: logPalette.mediaInteraction.surface,
        borderColor: logPalette.mediaInteraction.dark,
        eyebrowColor: logPalette.mediaInteraction.dark,
        titleColor: neutralPalette.textPrimary,
        metaColor: neutralPalette.textSecondary,
      },
      [TrackerDataType.ENGAGEMENT]: {
        backgroundColor: logPalette.engagement.surface,
        borderColor: logPalette.engagement.dark,
        eyebrowColor: logPalette.engagement.dark,
        titleColor: neutralPalette.textPrimary,
        metaColor: neutralPalette.textSecondary,
      },
      [TrackerDataType.CONTENT_LOG]: {
        backgroundColor: logPalette.content.surface,
        borderColor: logPalette.content.main,
        eyebrowColor: logPalette.content.main,
        titleColor: neutralPalette.textPrimary,
        metaColor: neutralPalette.textSecondary,
      },
    },
    modalCategory: {
      [TrackerDataType.SERVICE]: {
        titleColor: logPalette.service.main,
        buttonColor: logPalette.service.main,
        buttonHoverColor: logPalette.service.light,
      },
      [TrackerDataType.MEDIA_INTERACTION]: {
        titleColor: logPalette.mediaInteraction.dark,
        buttonColor: logPalette.mediaInteraction.dark,
        buttonHoverColor: logPalette.mediaInteraction.main,
      },
      [TrackerDataType.ENGAGEMENT]: {
        titleColor: logPalette.engagement.dark,
        buttonColor: logPalette.engagement.main,
        buttonHoverColor: logPalette.engagement.dark,
      },
      [TrackerDataType.CONTENT_LOG]: {
        titleColor: logPalette.content.main,
        buttonColor: logPalette.content.main,
        buttonHoverColor: logPalette.content.light,
      },
    },
    status: {
      [rowOptions.COMPLETED]: {
        backgroundColor: semanticPalette.success.fill,
        color: semanticPalette.success.text,
      },
      [rowOptions.PARTICIPATION]: {
        backgroundColor: appPalette.status.participation,
        color: neutralPalette.black,
      },
      [rowOptions.PROGRESS]: {
        backgroundColor: appPalette.status.inProgress,
        color: neutralPalette.black,
      },
      [rowOptions.CANCELLED]: {
        backgroundColor: appPalette.status.cancelled,
        color: neutralPalette.black,
      },
      [rowOptions.DRAFT_AUTOTRACKER]: {
        backgroundColor: appPalette.status.draftedAutotracker,
        color: neutralPalette.black,
      },
      default: {
        backgroundColor: neutralPalette.surfaceRaised,
        color: neutralPalette.textPrimary,
      },
    },
    ui: {
      shadows: {
        soft: `0 10px 24px ${alpha(logPalette.content.main, 0.06)}`,
        softInset: `0 0 0 1px ${alpha(logPalette.content.main, 0.08)}, 0 10px 24px ${alpha(
          logPalette.content.main,
          0.06
        )}`,
        topBar: `0 16px 40px ${alpha(logPalette.content.main, 0.08)}`,
        menu: `0 18px 36px ${alpha(logPalette.content.main, 0.18)}`,
        modal: `0 22px 54px ${alpha(logPalette.content.main, 0.22)}`,
        selectorButton: `0 8px 20px ${alpha(logPalette.content.main, 0.06)}`,
        primaryButton: `0 10px 24px ${alpha(logPalette.content.main, 0.18)}`,
      },
      topBar: {
        border: surfaceBorder,
        backgroundGradient: `linear-gradient(180deg, ${neutralPalette.surfaceCard} 0%, ${neutralPalette.surfacePage} 100%)`,
        actionButton: logPalette.service.main,
      },
      categoryBar: {
        containerBorder: surfaceBorder,
        containerBackground: `linear-gradient(180deg, ${neutralPalette.surfaceCard} 0%, ${neutralPalette.surfaceMuted} 100%)`,
        containerShadow: `0 8px 18px ${alpha(logPalette.content.main, 0.07)}`,
        headingText: neutralPalette.textPrimary,
        iconBackground: neutralPalette.white,
        selectedIconInsetShadow: `inset 0 0 0 1px ${alpha(neutralPalette.white, 0.14)}`,
        selectedText: neutralPalette.textSecondary,
        defaultText: alpha(neutralPalette.textSecondary, 0.68),
        selectedIconForeground: neutralPalette.white,
        unselectedIconBorder: surfaceBorderStrong,
        unselectedIconForeground: neutralPalette.textSecondary,
      },
      monthSelector: {
        label: logPalette.service.main,
        buttonBorder: surfaceBorder,
        buttonBackground: neutralPalette.white,
        buttonText: neutralPalette.textSecondary,
        buttonHoverBackground: neutralPalette.surfaceMuted,
      },
      monthGrid: {
        background: neutralPalette.surfaceRaised,
        weekdayLabel: neutralPalette.textPrimary,
      },
      topBarControls: {
        surfaceBackground: neutralPalette.white,
        searchActionIcon: neutralPalette.textSecondary,
        searchActionBackground: neutralPalette.surfaceMuted,
        searchCategoryIcon: neutralPalette.textPrimary,
        fieldBorder: componentPalette.input.border,
        buttonBorder: surfaceBorder,
        buttonText: neutralPalette.textPrimary,
        inputPlaceholder: neutralPalette.textHelper,
        inProgressBorder: alpha(appPalette.status.inProgress, 0.92),
        inProgressBackground: alpha(appPalette.status.inProgress, 0.55),
        toggleSelectedBackground: alpha(logPalette.service.main, 0.12),
        toggleSelectedText: logPalette.service.main,
      },
      searchResults: {
        fallbackCategory: neutralPalette.textSecondary,
        titleText: neutralPalette.textPrimary,
        metaText: neutralPalette.textSecondary,
        iconForeground: neutralPalette.white,
        iconShadow: `0 2px 5px ${alpha(neutralPalette.black, 0.08)}`,
      },
      dayCell: {
        selectedBorder: logPalette.content.main,
        defaultBorder: surfaceBorder,
        currentMonthBackground: alpha(neutralPalette.white, 0.96),
        outsideMonthBackground: alpha(neutralPalette.surfaceMuted, 0.74),
        currentMonthDayText: neutralPalette.textPrimary,
        outsideMonthDayText: alpha(neutralPalette.textHelper, 0.62),
        todayDot: logPalette.content.main,
        currentMonthMoreText: neutralPalette.textSecondary,
        outsideMonthMoreText: alpha(neutralPalette.textSecondary, 0.44),
      },
      selectedDayPanel: {
        title: logPalette.service.main,
        eyebrowText: neutralPalette.textPrimary,
        divider: surfaceBorder,
        washBackground: alpha(neutralPalette.surfaceMuted, 0.42),
        todayChipBackground: logPalette.content.surface,
        todayChipText: neutralPalette.textSecondary,
        summaryText: neutralPalette.textPrimary,
        emptyBackground: neutralPalette.surfaceMuted,
        emptyBorder: surfaceBorder,
        emptyText: neutralPalette.textSecondary,
        entryCardBorder: surfaceBorder,
        entryCardBackground: neutralPalette.white,
        entryCardMoreIcon: neutralPalette.textSecondary,
        entryCardActionText: neutralPalette.textSecondary,
        entryCardDateChipBackground: neutralPalette.surfaceRaised,
        entryCardDateChipText: neutralPalette.textSecondary,
        entryCardShadow: `0 2px 8px ${alpha(neutralPalette.black, 0.06)}`,
        entryCardHoverShadow: `0 6px 16px ${alpha(neutralPalette.black, 0.08)}`,
      },
      eventModal: {
        paperBackground: neutralPalette.surfaceCard,
        topBorderBackground: neutralPalette.surfaceCard,
        closeButton: neutralPalette.textSecondary,
        closeButtonBorder: surfaceBorder,
        closeButtonBackground: neutralPalette.surfaceMuted,
        sectionLabel: neutralPalette.textSecondary,
        accentChipBackground: logPalette.content.surface,
        accentChipText: logPalette.content.main,
        mutedChipBackground: neutralPalette.surfaceRaised,
        mutedChipText: neutralPalette.textSecondary,
        neutralChipBackground: neutralPalette.surfaceRaised,
        neutralChipText: neutralPalette.textSecondary,
        sectionCardBorder: surfaceBorder,
        outlinedChipBorder: surfaceBorder,
        outlinedChipBackground: neutralPalette.white,
        primaryText: neutralPalette.textPrimary,
        actionText: logPalette.service.main,
        avatarPrimaryBackground: logPalette.content.surfaceStrong,
        avatarSecondaryBackground: neutralPalette.surfaceRaised,
        avatarText: logPalette.content.main,
        avatarBorder: neutralPalette.white,
        avatarShadow: `0 2px 5px ${alpha(neutralPalette.black, 0.08)}`,
        notesText: typographyColors.primary,
        notesPlaceholder: typographyColors.muted,
        attachmentCardBackground: neutralPalette.white,
        attachmentCardBorder: surfaceBorder,
        attachmentCardHoverBorder: surfaceBorderStrong,
        attachmentIconFile: logPalette.content.main,
        attachmentIconLink: logPalette.mediaInteraction.dark,
        attachmentSecondaryText: typographyColors.muted,
        footerButtonBorder: surfaceBorder,
        footerButtonText: neutralPalette.textSecondary,
      },
    },
  } as const;
};

export const getCalendarCategoryColors = (theme: Theme) => getCalendarPalette(theme).categories;
export const getCalendarEventPillPalette = (theme: Theme) => getCalendarPalette(theme).eventPill;
export const getCalendarEntryCardPalette = (theme: Theme) => getCalendarPalette(theme).entryCard;
export const getCalendarModalCategoryPalette = (theme: Theme) => getCalendarPalette(theme).modalCategory;
export const getCalendarStatusPalette = (theme: Theme) => getCalendarPalette(theme).status;
export const getCalendarUiPalette = (theme: Theme) => getCalendarPalette(theme).ui;

export const getCalendarTypography = (theme: Theme) => ({
  eyebrow: {
    ...typography.overline,
    fontSize: fontSizes.overline,
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.overline,
    letterSpacing: typographyTokens.eyebrowText.letterSpacing,
    textTransform: 'uppercase',
  },
  eyebrowCompact: {
    ...typography.overline,
    fontSize: '0.62rem',
    fontWeight: fontWeights.bold,
    lineHeight: 1,
    letterSpacing: letterSpacing.eyebrow,
    textTransform: 'uppercase',
  },
  title: {
    ...semanticTypography.modalTitle,
    fontSize: '1.9rem',
    lineHeight: lineHeights.displayTight,
  },
  sectionTitle: {
    ...semanticTypography.subsectionTitle,
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.heading,
  },
  body: {
    ...semanticTypography.modalBody,
    fontSize: '0.88rem',
    lineHeight: 1.65,
  },
  bodySmall: {
    ...typography.body2,
    fontSize: '0.82rem',
    lineHeight: 1.55,
    color: typographyColors.muted,
  },
  bodyXs: {
    ...typography.caption,
    fontSize: '0.75rem',
    lineHeight: 1.3,
    color: typographyColors.muted,
  },
  chip: {
    ...semanticTypography.chipLabel,
    fontSize: fontSizes.chipLabel,
    lineHeight: 1,
  },
  chipCompact: {
    ...semanticTypography.chipLabel,
  },
});

const fallbackCategoryColors = {
  [TrackerDataType.SERVICE]: logPalette.service.main,
  [TrackerDataType.MEDIA_INTERACTION]: logPalette.mediaInteraction.dark,
  [TrackerDataType.ENGAGEMENT]: logPalette.engagement.dark,
  [TrackerDataType.CONTENT_LOG]: logPalette.content.main,
} as const;

export const calendarCategoryColors = fallbackCategoryColors;

export const calendarEventPillPalette = {
  [TrackerDataType.SERVICE]: {
    backgroundColor: alpha(logPalette.service.main, 0.14),
    borderColor: logPalette.service.main,
    color: neutralPalette.textPrimary,
    mutedBackgroundColor: alpha(logPalette.service.main, 0.09),
    mutedBorderColor: alpha(logPalette.service.main, 0.5),
    mutedColor: neutralPalette.textSecondary,
  },
  [TrackerDataType.MEDIA_INTERACTION]: {
    backgroundColor: alpha(logPalette.mediaInteraction.light, 0.22),
    borderColor: logPalette.mediaInteraction.dark,
    color: neutralPalette.textPrimary,
    mutedBackgroundColor: alpha(logPalette.mediaInteraction.light, 0.13),
    mutedBorderColor: alpha(logPalette.mediaInteraction.main, 0.55),
    mutedColor: neutralPalette.textSecondary,
  },
  [TrackerDataType.ENGAGEMENT]: {
    backgroundColor: alpha(logPalette.engagement.main, 0.14),
    borderColor: logPalette.engagement.dark,
    color: neutralPalette.textPrimary,
    mutedBackgroundColor: alpha(logPalette.engagement.main, 0.09),
    mutedBorderColor: alpha(logPalette.engagement.dark, 0.5),
    mutedColor: neutralPalette.textSecondary,
  },
  [TrackerDataType.CONTENT_LOG]: {
    backgroundColor: alpha(logPalette.content.main, 0.13),
    borderColor: logPalette.content.main,
    color: neutralPalette.textPrimary,
    mutedBackgroundColor: alpha(logPalette.content.main, 0.08),
    mutedBorderColor: alpha(logPalette.content.light, 0.48),
    mutedColor: neutralPalette.textSecondary,
  },
} as const;

export const calendarEntryCardPalette = {
  [TrackerDataType.SERVICE]: {
    backgroundColor: logPalette.service.surface,
    borderColor: logPalette.service.main,
    eyebrowColor: logPalette.service.main,
    titleColor: neutralPalette.textPrimary,
    metaColor: neutralPalette.textSecondary,
  },
  [TrackerDataType.MEDIA_INTERACTION]: {
    backgroundColor: logPalette.mediaInteraction.surface,
    borderColor: logPalette.mediaInteraction.dark,
    eyebrowColor: logPalette.mediaInteraction.dark,
    titleColor: neutralPalette.textPrimary,
    metaColor: neutralPalette.textSecondary,
  },
  [TrackerDataType.ENGAGEMENT]: {
    backgroundColor: logPalette.engagement.surface,
    borderColor: logPalette.engagement.dark,
    eyebrowColor: logPalette.engagement.dark,
    titleColor: neutralPalette.textPrimary,
    metaColor: neutralPalette.textSecondary,
  },
  [TrackerDataType.CONTENT_LOG]: {
    backgroundColor: logPalette.content.surface,
    borderColor: logPalette.content.main,
    eyebrowColor: logPalette.content.main,
    titleColor: neutralPalette.textPrimary,
    metaColor: neutralPalette.textSecondary,
  },
} as const;

export const calendarModalCategoryPalette = {
  [TrackerDataType.SERVICE]: {
    titleColor: logPalette.service.main,
    buttonColor: logPalette.service.main,
    buttonHoverColor: logPalette.service.light,
  },
  [TrackerDataType.MEDIA_INTERACTION]: {
    titleColor: logPalette.mediaInteraction.dark,
    buttonColor: logPalette.mediaInteraction.dark,
    buttonHoverColor: logPalette.mediaInteraction.main,
  },
  [TrackerDataType.ENGAGEMENT]: {
    titleColor: logPalette.engagement.dark,
    buttonColor: logPalette.engagement.main,
    buttonHoverColor: logPalette.engagement.dark,
  },
  [TrackerDataType.CONTENT_LOG]: {
    titleColor: logPalette.content.main,
    buttonColor: logPalette.content.main,
    buttonHoverColor: logPalette.content.light,
  },
} as const;

export const calendarStatusPalette = {
  [rowOptions.COMPLETED]: {
    backgroundColor: semanticPalette.success.fill,
    color: semanticPalette.success.text,
  },
  [rowOptions.PARTICIPATION]: {
    backgroundColor: appPalette.status.participation,
    color: neutralPalette.black,
  },
  [rowOptions.PROGRESS]: {
    backgroundColor: appPalette.status.inProgress,
    color: neutralPalette.black,
  },
  [rowOptions.CANCELLED]: {
    backgroundColor: appPalette.status.cancelled,
    color: neutralPalette.black,
  },
  [rowOptions.DRAFT_AUTOTRACKER]: {
    backgroundColor: appPalette.status.draftedAutotracker,
    color: neutralPalette.black,
  },
  default: {
    backgroundColor: neutralPalette.surfaceRaised,
    color: neutralPalette.textPrimary,
  },
} as const;

export const calendarUiPalette = {
  shadows: {
    soft: `0 10px 24px ${alpha(logPalette.content.main, 0.06)}`,
    softInset: `0 0 0 1px ${alpha(logPalette.content.main, 0.08)}, 0 10px 24px ${alpha(logPalette.content.main, 0.06)}`,
    topBar: `0 16px 40px ${alpha(logPalette.content.main, 0.08)}`,
    menu: `0 18px 36px ${alpha(logPalette.content.main, 0.18)}`,
    modal: `0 22px 54px ${alpha(logPalette.content.main, 0.22)}`,
    selectorButton: `0 8px 20px ${alpha(logPalette.content.main, 0.06)}`,
    primaryButton: `0 10px 24px ${alpha(logPalette.content.main, 0.18)}`,
  },
  topBar: {
    border: componentPalette.card.border,
    backgroundGradient: `linear-gradient(180deg, ${neutralPalette.surfaceCard} 0%, ${neutralPalette.surfacePage} 100%)`,
    actionButton: logPalette.service.main,
  },
  categoryBar: {
    containerBorder: componentPalette.card.border,
    containerBackground: `linear-gradient(180deg, ${neutralPalette.surfaceCard} 0%, ${neutralPalette.surfaceMuted} 100%)`,
    containerShadow: `0 8px 18px ${alpha(logPalette.content.main, 0.07)}`,
    headingText: neutralPalette.textPrimary,
    iconBackground: neutralPalette.white,
    selectedIconInsetShadow: `inset 0 0 0 1px ${alpha(neutralPalette.white, 0.14)}`,
    selectedText: neutralPalette.textSecondary,
    defaultText: alpha(neutralPalette.textSecondary, 0.68),
    selectedIconForeground: neutralPalette.white,
    unselectedIconBorder: neutralPalette.borderStrong,
    unselectedIconForeground: neutralPalette.textSecondary,
  },
  monthSelector: {
    label: logPalette.service.main,
    buttonBorder: componentPalette.card.border,
    buttonBackground: neutralPalette.white,
    buttonText: neutralPalette.textSecondary,
    buttonHoverBackground: neutralPalette.surfaceMuted,
  },
  monthGrid: {
    background: neutralPalette.surfaceRaised,
    weekdayLabel: neutralPalette.textPrimary,
  },
  topBarControls: {
    surfaceBackground: neutralPalette.white,
    searchActionIcon: neutralPalette.textSecondary,
    searchActionBackground: neutralPalette.surfaceMuted,
    searchCategoryIcon: neutralPalette.textPrimary,
    fieldBorder: componentPalette.input.border,
    buttonBorder: componentPalette.card.border,
    buttonText: neutralPalette.textPrimary,
    inputPlaceholder: neutralPalette.textHelper,
    inProgressBorder: alpha(appPalette.status.inProgress, 0.92),
    inProgressBackground: alpha(appPalette.status.inProgress, 0.55),
    toggleSelectedBackground: alpha(logPalette.service.main, 0.12),
    toggleSelectedText: logPalette.service.main,
  },
  searchResults: {
    fallbackCategory: neutralPalette.textSecondary,
    titleText: neutralPalette.textPrimary,
    metaText: neutralPalette.textSecondary,
    iconForeground: neutralPalette.white,
    iconShadow: `0 2px 5px ${alpha(neutralPalette.black, 0.08)}`,
  },
  dayCell: {
    selectedBorder: logPalette.content.main,
    defaultBorder: componentPalette.card.border,
    currentMonthBackground: alpha(neutralPalette.white, 0.96),
    outsideMonthBackground: alpha(neutralPalette.surfaceMuted, 0.74),
    currentMonthDayText: neutralPalette.textPrimary,
    outsideMonthDayText: alpha(neutralPalette.textHelper, 0.62),
    todayDot: logPalette.content.main,
    currentMonthMoreText: neutralPalette.textSecondary,
    outsideMonthMoreText: alpha(neutralPalette.textSecondary, 0.44),
  },
  selectedDayPanel: {
    title: logPalette.service.main,
    eyebrowText: neutralPalette.textPrimary,
    divider: componentPalette.card.border,
    washBackground: alpha(neutralPalette.surfaceMuted, 0.42),
    todayChipBackground: logPalette.content.surface,
    todayChipText: neutralPalette.textSecondary,
    summaryText: neutralPalette.textPrimary,
    emptyBackground: neutralPalette.surfaceMuted,
    emptyBorder: componentPalette.card.border,
    emptyText: neutralPalette.textSecondary,
    entryCardBorder: componentPalette.card.border,
    entryCardBackground: neutralPalette.white,
    entryCardMoreIcon: neutralPalette.textSecondary,
    entryCardActionText: neutralPalette.textSecondary,
    entryCardDateChipBackground: neutralPalette.surfaceRaised,
    entryCardDateChipText: neutralPalette.textSecondary,
    entryCardShadow: `0 2px 8px ${alpha(neutralPalette.black, 0.06)}`,
    entryCardHoverShadow: `0 6px 16px ${alpha(neutralPalette.black, 0.08)}`,
  },
  eventModal: {
    paperBackground: neutralPalette.surfaceCard,
    topBorderBackground: neutralPalette.surfaceCard,
    closeButton: neutralPalette.textSecondary,
    closeButtonBorder: componentPalette.card.border,
    closeButtonBackground: neutralPalette.surfaceMuted,
    sectionLabel: neutralPalette.textSecondary,
    accentChipBackground: logPalette.content.surface,
    accentChipText: logPalette.content.main,
    mutedChipBackground: neutralPalette.surfaceRaised,
    mutedChipText: neutralPalette.textSecondary,
    neutralChipBackground: neutralPalette.surfaceRaised,
    neutralChipText: neutralPalette.textSecondary,
    sectionCardBorder: componentPalette.card.border,
    outlinedChipBorder: componentPalette.card.border,
    outlinedChipBackground: neutralPalette.white,
    primaryText: neutralPalette.textPrimary,
    actionText: logPalette.service.main,
    avatarPrimaryBackground: logPalette.content.surfaceStrong,
    avatarSecondaryBackground: neutralPalette.surfaceRaised,
    avatarText: logPalette.content.main,
    avatarBorder: neutralPalette.white,
    avatarShadow: `0 2px 5px ${alpha(neutralPalette.black, 0.08)}`,
    notesText: typographyColors.primary,
    notesPlaceholder: typographyColors.muted,
    attachmentCardBackground: neutralPalette.white,
    attachmentCardBorder: componentPalette.card.border,
    attachmentCardHoverBorder: neutralPalette.borderStrong,
    attachmentIconFile: logPalette.content.main,
    attachmentIconLink: logPalette.mediaInteraction.dark,
    attachmentSecondaryText: typographyColors.muted,
    footerButtonBorder: componentPalette.card.border,
    footerButtonText: neutralPalette.textSecondary,
  },
} as const;
